import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Check, X, Copy } from "lucide-react";
import { Button, Modal, Select, Table } from "../../../components";
import {
  PLATFORM_COMPANY_REQUESTS_URL,
  PLATFORM_APPROVE_REQUEST_URL,
  PLATFORM_REJECT_REQUEST_URL,
} from "../../../features/UrlHelper";

/**
 * Ziadosti z marketingovej stranky (DEMO aj ZAUJEM v jednej evidencii).
 *
 * Schvalenie zalozi firmu, cennik STANDARD a admina s dociasnym heslom
 * (pri DEMO aj ukazkove data) a posle mail s pristupmi. Dociasne heslo sa
 * zobrazi aj tu -- ked mail nedorazi, da sa odovzdat inak.
 */
const TYP_LABEL = { DEMO: "Demo", ZAUJEM: "Záujem" };
const STAV_LABEL = { NEW: "Nová", APPROVED: "Schválená", REJECTED: "Odmietnutá" };
const DEMO_PLATNOST_DNI = 30;

const TYP_FILTER = [
  { value: "", label: "Všetky typy" },
  { value: "DEMO", label: "Demo" },
  { value: "ZAUJEM", label: "Záujem" },
];

const STAV_FILTER = [
  { value: "", label: "Všetky stavy" },
  { value: "NEW", label: "Nové" },
  { value: "APPROVED", label: "Schválené" },
  { value: "REJECTED", label: "Odmietnuté" },
];

const formatDatum = (d) => (d ? new Date(d).toLocaleDateString("sk-SK") : "—");

function RequestsSection() {
  const [filter, setFilter] = useState({ typ: "", status: "NEW" });
  const [ziadosti, setZiadosti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chyba, setChyba] = useState("");

  const [schvalovana, setSchvalovana] = useState(null);
  const [platnostDni, setPlatnostDni] = useState(DEMO_PLATNOST_DNI);
  const [slug, setSlug] = useState("");
  const [odosiela, setOdosiela] = useState(false);
  const [chybaModalu, setChybaModalu] = useState("");
  const [vysledok, setVysledok] = useState(null);
  const [odmietana, setOdmietana] = useState(null);

  const nacitaj = useCallback(async () => {
    setLoading(true);
    setChyba("");
    try {
      const { data } = await axios.get(PLATFORM_COMPANY_REQUESTS_URL(filter));
      setZiadosti(Array.isArray(data) ? data : []);
    } catch (err) {
      setChyba(err?.response?.data?.message || "Žiadosti sa nepodarilo načítať.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    nacitaj();
  }, [nacitaj]);

  const otvorSchvalenie = (row) => {
    setSchvalovana(row);
    setPlatnostDni(DEMO_PLATNOST_DNI);
    setSlug(row.pozadovany_slug || "");
    setChybaModalu("");
  };

  const schval = async (e) => {
    e.preventDefault();
    setOdosiela(true);
    setChybaModalu("");
    try {
      const { data } = await axios.post(PLATFORM_APPROVE_REQUEST_URL(schvalovana.id), {
        platnostDni: schvalovana.typ === "DEMO" ? Number(platnostDni) : undefined,
        slug: slug.trim().toLowerCase() || undefined,
      });
      setSchvalovana(null);
      setVysledok({ ...data, email: schvalovana.email });
      await nacitaj();
    } catch (err) {
      setChybaModalu(err?.response?.data?.message || "Schválenie zlyhalo.");
    } finally {
      setOdosiela(false);
    }
  };

  const odmietni = async () => {
    setOdosiela(true);
    try {
      await axios.post(PLATFORM_REJECT_REQUEST_URL(odmietana.id));
      setOdmietana(null);
      await nacitaj();
    } catch (err) {
      alert(err?.response?.data?.message || "Odmietnutie zlyhalo.");
    } finally {
      setOdosiela(false);
    }
  };

  const adresa = (s) => `${window.location.origin}/${s}`;

  return (
    <div className="admin__section">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Žiadosti o prevádzku</h2>
      </div>

      {chyba && <p className="platform__error">{chyba}</p>}

      <div className="admin__table-wrapper">
        <Table
          columns={[
            { key: "created_at", label: "Prijatá", render: (v) => formatDatum(v) },
            { key: "typ", label: "Typ", render: (v) => TYP_LABEL[v] || v },
            { key: "nazov_prevadzky", label: "Prevádzka" },
            {
              key: "kontaktna_osoba",
              label: "Kontakt",
              render: (v, row) => (
                <span>
                  {v}
                  <br />
                  <small>{row.email}{row.telefon ? ` · ${row.telefon}` : ""}</small>
                </span>
              ),
            },
            { key: "pozadovany_slug", label: "Adresa", render: (v) => v || "—" },
            {
              key: "status",
              label: "Stav",
              render: (v, row) =>
                v === "APPROVED" && row.company_slug ? (
                  <a href={adresa(row.company_slug)} target="_blank" rel="noopener noreferrer">
                    {STAV_LABEL[v]} ({row.company_slug})
                  </a>
                ) : (
                  STAV_LABEL[v] || v
                ),
            },
            {
              key: "actions",
              label: "Akcie",
              sortable: false,
              render: (_, row) =>
                row.status === "NEW" ? (
                  <div className="admin__table-actions">
                    <button className="admin__icon-btn" onClick={() => otvorSchvalenie(row)} title="Schváliť">
                      <Check size={16} />
                    </button>
                    <button className="admin__icon-btn admin__icon-btn--danger" onClick={() => setOdmietana(row)} title="Odmietnuť">
                      <X size={16} />
                    </button>
                  </div>
                ) : null,
            },
          ]}
          data={ziadosti}
          toolbarContent={
            <>
              <div className="platform__filter">
                <Select
                  options={TYP_FILTER}
                  value={filter.typ}
                  onChange={(typ) => setFilter((f) => ({ ...f, typ }))}
                />
              </div>
              <div className="platform__filter">
                <Select
                  options={STAV_FILTER}
                  value={filter.status}
                  onChange={(status) => setFilter((f) => ({ ...f, status }))}
                />
              </div>
            </>
          }
          searchPlaceholder="Hľadať žiadosť..."
          emptyMessage={loading ? "Načítavam..." : "Žiadne žiadosti"}
        />
      </div>

      {/* Schvalenie */}
      <Modal isOpen={!!schvalovana} onClose={() => setSchvalovana(null)} title="Schváliť žiadosť">
        {schvalovana && (
          <form className="admin__form" onSubmit={schval}>
            <p>
              <strong>{schvalovana.nazov_prevadzky}</strong> ({TYP_LABEL[schvalovana.typ]})
              {schvalovana.poznamka && (
                <>
                  <br />
                  <small>{schvalovana.poznamka}</small>
                </>
              )}
            </p>
            {schvalovana.typ === "DEMO" && (
              <div className="admin__form-field">
                <label className="admin__form-label">Platnosť demo prevádzky (dní)</label>
                <input
                  className="admin__form-input"
                  type="number"
                  min={1}
                  max={365}
                  value={platnostDni}
                  onChange={(e) => setPlatnostDni(e.target.value)}
                  required
                />
              </div>
            )}
            <div className="admin__form-field">
              <label className="admin__form-label">
                Adresa (slug){schvalovana.typ === "DEMO" ? " — predpona demo- sa doplní" : ""}
              </label>
              <input
                className="admin__form-input"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="odvodí sa z názvu"
                maxLength={40}
              />
            </div>
            {chybaModalu && <p className="platform__error" role="alert">{chybaModalu}</p>}
            <div className="admin__form-actions">
              <Button variant="ghost" type="button" onClick={() => setSchvalovana(null)}>
                Zrušiť
              </Button>
              <Button variant="primary" type="submit" disabled={odosiela}>
                {odosiela ? "Zakladám prevádzku..." : "Schváliť a založiť"}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Vysledok schvalenia */}
      <Modal isOpen={!!vysledok} onClose={() => setVysledok(null)} title="Prevádzka založená">
        {vysledok && (
          <div className="admin__form">
            <p>
              Prístupy sme poslali na <strong>{vysledok.email}</strong>. Ak mail nedorazí, odovzdajte ich inak:
            </p>
            <dl className="platform__credentials">
              <dt>Adresa</dt>
              <dd>
                <a href={adresa(vysledok.company.slug)} target="_blank" rel="noopener noreferrer">
                  {adresa(vysledok.company.slug)}
                </a>
              </dd>
              <dt>Meno</dt>
              <dd>{vysledok.admin.username}</dd>
              <dt>Dočasné heslo</dt>
              <dd>
                <code>{vysledok.admin.tempPassword}</code>{" "}
                <button
                  type="button"
                  className="admin__icon-btn"
                  title="Kopírovať"
                  onClick={() => navigator.clipboard?.writeText(vysledok.admin.tempPassword)}
                >
                  <Copy size={14} />
                </button>
              </dd>
              {vysledok.company.valid_to && (
                <>
                  <dt>Platná do</dt>
                  <dd>{formatDatum(vysledok.company.valid_to)}</dd>
                </>
              )}
            </dl>
            <div className="admin__form-actions">
              <Button variant="primary" onClick={() => setVysledok(null)}>
                Zavrieť
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Odmietnutie */}
      <Modal isOpen={!!odmietana} onClose={() => setOdmietana(null)} title="Odmietnuť žiadosť">
        <p>Naozaj odmietnuť žiadosť prevádzky <strong>{odmietana?.nazov_prevadzky}</strong>?</p>
        <div className="admin__form-actions">
          <Button variant="ghost" onClick={() => setOdmietana(null)}>
            Zrušiť
          </Button>
          <Button
            variant="primary"
            onClick={odmietni}
            disabled={odosiela}
            style={{ backgroundColor: "var(--c-error)", borderColor: "var(--c-error)" }}
          >
            Odmietnuť
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default RequestsSection;
